import { Injectable } from '@angular/core';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Auth } from '@angular/fire/auth';
import {
  MFA_MAX_AGE_SECONDS,
  MFA_PENDING_UID_KEY,
  isMfaClaimFresh as isMfaClaimFreshFrom,
} from './mfa-claim-freshness';

export interface TwoFactorSetupResult {
  secret: string;
  qrCodeUrl: string;
  manualEntryKey: string;
}

export interface TwoFactorVerifyResult {
  valid: boolean;
  message: string;
  /** #867 — horodatage de la vérification, posé en custom claim sur le token. */
  mfaAt?: number | null;
}

export interface TwoFactorEnableResult {
  enabled: boolean;
  backupCodes: string[];
  message: string;
  /** #867 — l'activation vaut vérification : le token est estampillé aussi. */
  mfaAt?: number | null;
}

export interface TwoFactorStatus {
  enabled: boolean;
  enrolledAt: string | null;
  lastUsedAt: string | null;
  backupCodesRemaining: number;
}

@Injectable({ providedIn: 'root' })
export class TwoFactorService {

  private readonly SESSION_KEY = '2fa_verified';
  private readonly PENDING_UID_KEY = MFA_PENDING_UID_KEY;

  /**
   * Fenêtre de fraîcheur, alignée sur MFA_MAX_AGE_SECONDS côté functions (12 h).
   *
   * #1267 — la valeur ET le calcul vivent désormais dans `mfa-claim-freshness`,
   * un module PUR : `PortalAuthGuard` en a besoin sans pouvoir traîner le graphe
   * de ce service (qui dépend de `Functions`). Cet alias reste pour les
   * appelants existants.
   */
  static readonly MFA_MAX_AGE_SECONDS = MFA_MAX_AGE_SECONDS;

  constructor(private functions: Functions, private auth: Auth) {}

  // ── Callable wrappers ──

  async setup(): Promise<TwoFactorSetupResult> {
    const callable = httpsCallable(this.functions, 'setupTwoFactor');
    const result = await callable({});
    return result.data as TwoFactorSetupResult;
  }

  async verify(token: string): Promise<TwoFactorVerifyResult> {
    const callable = httpsCallable(this.functions, 'verifyTwoFactor');
    const result = await callable({ token });
    const data = result.data as TwoFactorVerifyResult;
    if (data?.valid) await this.refreshToken();
    return data;
  }

  /**
   * #1943 — seul le code est envoyé : le serveur active le secret d'enrôlement
   * qu'il a lui-même posé à `setup()`, jamais un secret venu du navigateur.
   */
  async enable(token: string): Promise<TwoFactorEnableResult> {
    const callable = httpsCallable(this.functions, 'enableTwoFactor');
    const result = await callable({ token });
    const data = result.data as TwoFactorEnableResult;
    if (data?.enabled) await this.refreshToken();
    return data;
  }

  /**
   * #867 — récupère le token estampillé de la claim `mfaAt`.
   *
   * Sans ce rafraîchissement, la vérification resterait invisible du serveur
   * jusqu'à ce que Firebase renouvelle le jeton de lui-même (jusqu'à une heure),
   * et les opérations privilégiées seraient refusées entre-temps. Best-effort :
   * un échec ne doit pas empêcher d'entrer dans le backoffice, il retarde
   * seulement la prise en compte de la claim.
   */
  private async refreshToken(): Promise<void> {
    try {
      await this.auth.currentUser?.getIdToken(true);
    } catch (err) {
      console.warn('[2FA] Rafraîchissement du token impossible :', err);
    }
  }

  async reset(token: string): Promise<{ success: boolean; message: string }> {
    const callable = httpsCallable(this.functions, 'resetTwoFactor');
    const result = await callable({ token });
    return result.data as { success: boolean; message: string };
  }

  async getStatus(): Promise<TwoFactorStatus> {
    const callable = httpsCallable(this.functions, 'getTwoFactorStatus');
    const result = await callable({});
    return result.data as TwoFactorStatus;
  }

  // ── Session management ──

  mark2FAVerified(): void {
    sessionStorage.setItem(this.SESSION_KEY, 'true');
  }

  is2FAVerified(): boolean {
    return sessionStorage.getItem(this.SESSION_KEY) === 'true';
  }

  /**
   * #867 étape 2 — la vérification portée par le TOKEN est-elle encore fraîche ?
   *
   * `is2FAVerified()` ne consulte que `sessionStorage`, que le serveur ne voit
   * pas : depuis que `firestore.rules` exige la claim `mfaAt`, une session dont
   * la claim a passé les 12 h continuerait à afficher le backoffice pendant que
   * Firestore refuserait chaque lecture. On tranche donc sur le token.
   *
   * Trois cas, et un seul change quelque chose :
   *   - claim fraîche       → vérifié (le serveur l'accepte, on aussi) ;
   *   - claim PÉRIMÉE       → non vérifié → retour à la saisie du code. C'est le
   *                           cas qui, sans ça, casserait silencieusement ;
   *   - claim ABSENTE       → on ne décide pas : `null`, l'appelant retombe sur
   *                           `is2FAVerified()`. Indispensable tant que la claim
   *                           n'a pas circulé (jetons émis avant #866), sinon on
   *                           renverrait tout le monde vers /verify-2fa.
   *
   * @returns `true`/`false` si le token tranche, `null` s'il ne porte rien.
   */
  async isMfaClaimFresh(): Promise<boolean | null> {
    try {
      const user = this.auth.currentUser;
      if (!user) return null;
      const result = await user.getIdTokenResult();
      // #1267 — le calcul vit dans `mfa-claim-freshness` (module pur), miroir
      // d'`isMfaFresh` (functions/src/utils/mfa-claims.ts) et du helper
      // hasRecentMfa() des rules : 12 h, une minute de tolérance vers le futur.
      return isMfaClaimFreshFrom(result.claims as Record<string, unknown>);
    } catch (err) {
      // Une lecture de token qui échoue ne doit pas bloquer l'accès : on rend la
      // main au comportement historique.
      console.warn('[2FA] Lecture de la claim mfaAt impossible :', err);
      return null;
    }
  }

  clear2FASession(): void {
    sessionStorage.removeItem(this.SESSION_KEY);
    sessionStorage.removeItem(this.PENDING_UID_KEY);
  }

  setPendingUid(uid: string): void {
    sessionStorage.setItem(this.PENDING_UID_KEY, uid);
  }

  getPendingUid(): string | null {
    return sessionStorage.getItem(this.PENDING_UID_KEY);
  }
}
